// src/components/DownloadChartModal.js
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "../styles/Guides.css";
import { httpsCallable } from 'firebase/functions';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { functions } from '../firebase';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { PDFDocument } from 'pdf-lib';

/* ───────── props ─────────
   open      : Boolean  – show / hide
   onClose   : Function – called on × or after submit
   onSubmit  : Function – optional callback(formData)
   fileSrc   : String   – path to png / pdf / etc.
   fileName  : String   – name shown in browser download tray
   ───────────────────────── */
export default function DownloadChartModal({
  open,
  onClose,
  fileSrc,
  fileName,
  onSubmit,
  storagePath,     // e.g. 'charts/bolt-chart-asme-b16-5.png' or 'charts/amp-chart.png'
  asPDF = true     // default: produce a PDF; set false to download the image directly
}) {
  const storage = getStorage(); // your default Firebase app

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  

  /* hooks must run every render */
  const [form, setForm] = useState({
    firstName: "",
    lastName:  "",
    phone:     "",
    email:     "",
    company:   "",
    businessType: "",
  });

  /* freeze background scroll while modal is open */
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
   if (!open) return;
   const auth = getAuth();
   // If already authed, this resolves immediately; otherwise anonymous sign-in
   signInAnonymously(auth).catch(() => {});
 }, [open]);

  /* bail out after hooks so ESLint is happy */
  if (!open) return null;

  /* helpers */
  const handleChange = ({ target:{ name, value } }) =>
    setForm(prev => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  setError('');

  try {
    // 1) Log the lead (await, but don’t block the download if it fails)
    try {
      const logLead = httpsCallable(functions, 'logDownloadLead');
      await logLead(form);
    } catch (err) {
      console.warn('logDownloadLead failed:', err);
    }

    // 2) Resolve a signed download URL from Firebase Storage
    //    Expect storagePath like: 'charts/bolt-chart-asme-b16-5.pdf'
    let url = fileSrc || '';
    if (storagePath) {
      url = await getDownloadURL(ref(storage, storagePath));
    }
    if (!url) throw new Error('No chart source provided');

    // Open in a new tab without navigating the current page
    const a = document.createElement('a');
    a.href = url;                       // resolved Storage download URL
    a.target = '_blank';                // new tab
    a.rel = 'noopener noreferrer';      // security best practice
    // IMPORTANT: do NOT set a.download here if you want it to render in a tab
    document.body.appendChild(a);
    a.click();
    a.remove();

    onSubmit?.(form);
    onClose();
  } catch (err) {
    console.error(err);
    setError('Download failed. Please try again.');
  } finally {
    setSubmitting(false);
  }
};




  const base64ToBlob = (raw, contentType = 'application/pdf') => {
  // Support data URLs and URL-safe base64
  const base64 = raw.startsWith('data:')
    ? raw.split(',')[1]
    : raw.replace(/-/g, '+').replace(/_/g, '/');
  const byteChars = atob(base64);
   const byteNumbers = new Array(byteChars.length);
   for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
   const byteArray = new Uint8Array(byteNumbers);
   return new Blob([byteArray], { type: contentType });
 };

 const triggerBlobDownload = (blob, name) => {
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = name || 'download.pdf';
   document.body.appendChild(a);
   a.click();
   a.remove();
   URL.revokeObjectURL(url);
 };

  /* modal markup */
  const modalJSX = (
    <div className="modal-backdrop open" onClick={onClose}>
      <div className="modal open" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>Get the full sizing chart</h2>
        <p className="modal-sub">
          Drop in your details &amp; we’ll start the download.
        </p>

        <form className="chart-form" onSubmit={handleSubmit}>
          <div className="field-duo">
            <input required name="firstName" placeholder="First name"
                   value={form.firstName} onChange={handleChange}/>
            <input required name="lastName"  placeholder="Last name"
                   value={form.lastName}  onChange={handleChange}/>
          </div>

          <input name="company"       placeholder="Company"
                 value={form.company} onChange={handleChange}/>
          <input name="businessType"  placeholder="Business type / industry"
                 value={form.businessType} onChange={handleChange}/>
          <input name="phone" type="tel"   placeholder="Phone"
                 value={form.phone} onChange={handleChange}/>
          <input required name="email" type="email" placeholder="Email address"
                 value={form.email} onChange={handleChange}/>

           <button type="submit" className="cta-btn large" disabled={submitting}>
             {submitting ? 'Preparing your PDF…' : 'Download now'}
           </button>
            {error && <p className="error-text" role="alert">{error}</p>}
          </form>
      </div>
    </div>
  );

  /* render outside any transformed ancestor */
  return ReactDOM.createPortal(modalJSX, document.body);
}
